import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import DemoRequest from "../components/sections/DemoRequest";
import { useI18n } from "../i18n/I18nProvider";
import { getSolution, getSolutions } from "../i18n/content";

export default function SolutionsPage() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const detail = slug ? getSolution(slug, locale) : null;
  if (slug && detail) return <SolutionDetail s={detail} />;
  return <SolutionsList />;
}

function SolutionsList() {
  const { t, locale } = useI18n();
  const visible = getSolutions(locale).filter((s) => !s.hidden);
  return (
    <div data-testid="solutions-page">
      <section className="py-24 sm:py-32 border-b border-zinc-200">
        <div className="container-oj">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("solutions_page.eyebrow_all")}</div>
          <h1 className="font-display mt-4 text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] text-oj-ink max-w-4xl">
            {t("solutions_page.h1_a")} {t("solutions_page.h1_b")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-600">
            {t("solutions_page.intro")}
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-zinc-50">
        <div className="container-oj grid grid-cols-1 md:grid-cols-2 gap-6">
          {visible.map((s, i) => (
            <Link
              key={s.slug}
              to={`/solutions/${s.slug}`}
              data-testid={`solutions-list-${s.slug}`}
              className="group relative rounded-2xl p-10 overflow-hidden text-white transition-transform hover:-translate-y-1 duration-300"
              style={{ backgroundColor: s.color }}
            >
              <div className="absolute inset-0 noise opacity-30 mix-blend-overlay pointer-events-none" />
              <div className="relative flex items-center justify-between">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] opacity-80">/{String(i + 1).padStart(2, "0")} · {s.short}</div>
                <ArrowUpRight className="size-5 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
              <h3 className="relative mt-12 font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">{s.name}</h3>
              <p className="relative mt-3 text-white/90 text-lg max-w-md">{s.tagline}</p>
              <p className="relative mt-5 text-white/80 max-w-md">{s.description}</p>
            </Link>
          ))}
        </div>
      </section>
      <DemoRequest />
    </div>
  );
}

function SolutionDetail({ s }) {
  const { t, locale } = useI18n();
  const others = getSolutions(locale).filter((x) => x.slug !== s.slug && !x.hidden).slice(0, 4);
  return (
    <div data-testid={`solution-detail-${s.slug}`}>
      <section className="relative overflow-hidden text-white" style={{ background: s.color }}>
        <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
        <div className="container-oj relative py-24 sm:py-32">
          <Link to="/solutions" className="text-white/80 text-sm hover:text-white inline-flex items-center gap-1">{t("solutions_page.back")}</Link>
          <div className="font-mono mt-8 text-[11px] uppercase tracking-[0.25em] opacity-80">/{s.short}</div>
          <h1 className="font-display mt-4 text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] max-w-4xl">{s.name}</h1>
          <p className="mt-5 text-xl sm:text-2xl text-white/90 max-w-2xl">{s.tagline}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/#demo" className="inline-flex items-center gap-1.5 bg-white text-oj-ink font-semibold px-6 py-4 rounded-md hover:bg-zinc-100 transition-colors">
              {t("solutions_page.book_demo")} <ArrowUpRight className="size-5" />
            </Link>
            <Link to="/configurator" className="inline-flex items-center gap-1.5 border border-white/30 bg-white/5 backdrop-blur text-white font-semibold px-6 py-4 rounded-md hover:bg-white/10">
              {t("solutions_page.configure_hardware")}
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-oj grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-oj-ink">{t("solutions_page.what_you_get")}</h2>
            <p className="mt-4 text-lg text-zinc-600 max-w-xl">{s.description}</p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {s.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-zinc-800">
                  <span className="size-5 rounded-full grid place-items-center text-white shrink-0 mt-0.5" style={{ background: s.color }}>
                    <Check className="size-3" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <aside className="lg:col-span-5 rounded-2xl border border-zinc-200 p-8 bg-zinc-50">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">{t("solutions_page.pairs_with")}</div>
            <ul className="mt-4 space-y-3">
              {others.map((x) => (
                <li key={x.slug}>
                  <Link to={`/solutions/${x.slug}`} className="flex items-center justify-between rounded-md p-3 bg-white border border-zinc-200 hover:border-zinc-900 transition-colors">
                    <span className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: x.color }} />
                      <span className="font-semibold text-oj-ink">{x.name}</span>
                    </span>
                    <ArrowUpRight className="size-4 text-zinc-500" />
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      <DemoRequest />
    </div>
  );
}
